import { useMemo, useRef, useState, FC, memo } from 'react';

import { TEXTS } from 'application/pages/AdsPage/renderer/texts';
import { ModalTypes, ModalProps } from 'application/providers/ModalProvider';

import { AdCampaignTypeEnum } from '@entities/media';
import { ReplicationRole } from '@shared/api/graphql/generated';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';

import styles from './index.module.scss';

const AdUpdateConfirmModal: FC<ModalProps[ModalTypes.AD_CAMPAIGN_CONFIRM_UPDATE]> = ({
  adCampaign,
  isAdCampaignInstant,
  isAdHasOneTurnedOnPoint,
  onSubmit,
  close,
  awaitSubmit = false,
}) => {
  const pending = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSlaveAdCampaign = adCampaign.replicationRole === ReplicationRole.Slave;

  const warningBlock = useMemo(() => {
    if (!adCampaign.timingSettings.endDate || adCampaign.type === AdCampaignTypeEnum.Instant) {
      return null;
    }

    if (DateUtils.isDateBefore(new Date(adCampaign.timingSettings.endDate), DateUtils.NOW)) {
      return TEXTS.SAVE_AD_MODAL_WARNING_BEFORE_TODAY(
        DateUtils.getFormatDateFrom(new Date(adCampaign.timingSettings.endDate), 'dd.MM.yyyy')
      );
    }

    return null;
  }, [adCampaign.timingSettings]);

  const onSubmitClick = async () => {
    if (pending.current) return;
    if (!awaitSubmit) {
      onSubmit();
      if (!isSlaveAdCampaign) close();
      return;
    }
    pending.current = true;
    setIsSaving(true);
    try {
      const ok = await onSubmit();
      if (ok !== false) close();
    } finally {
      pending.current = false;
      setIsSaving(false);
    }
  };

  return (
    <ModalScreen
      title={isSlaveAdCampaign ? TEXTS.SAVE_SLAVE_AD_MODAL_TITLE : TEXTS.SAVE_AD_MODAL_TITLE}
      onClose={() => {
        if (!pending.current) close();
      }}
      className={styles.AdUpdateConfirmModal}
      actionButtons={[
        <Button
          key="button_confirm_ad_campaign_create_modal"
          elementId="button_confirm_ad_campaign_create_modal"
          type={ButtonType.PRIMARY}
          isLoading={isSaving}
          onClick={onSubmitClick}>
          {isSlaveAdCampaign ? TEXTS.BUTTON_SAVE_SLAVE_AD_TITLE : TEXTS.BUTTON_SAVE_AD_TITLE}
        </Button>,
        <Button
          key="button_cancel_ad_campaign_create_modal"
          elementId="button_cancel_ad_campaign_create_modal"
          type={ButtonType.SECONDARY}
          isDisabled={isSaving}
          onClick={() => close()}>
          {TEXTS.MODAL_AD_REMOVE_BUTTON_CANCEL}
        </Button>,
      ]}>
      <div className={styles.AdUpdateConfirmModalDesc}>
        {isAdCampaignInstant && isAdHasOneTurnedOnPoint ? (
          <>
            <div>{TEXTS.SAVE_INSTANT_AD_MODAL_DESCRIPTION}</div>
            {isSlaveAdCampaign && <p>{TEXTS.SAVE_SLAVE_AD_MODAL_DESCRIPTION}</p>}
          </>
        ) : (
          <>
            {isSlaveAdCampaign ? TEXTS.SAVE_SLAVE_AD_MODAL_DESCRIPTION : TEXTS.SAVE_AD_MODAL_DESCRIPTION}
            {warningBlock && (
              <Info
                type={InfoTypes.ERROR}
                className={styles.AdUpdateConfirmModalWarningInfo}>
                {warningBlock}
              </Info>
            )}
          </>
        )}
      </div>
    </ModalScreen>
  );
};

export default memo(AdUpdateConfirmModal);
